<template>
  <transition-group
    name="ae-fade"
    tag="div"
    class="-overlay-list"
    :class="{ '-storyboard': isStoryboarding }"
  >
    <div
      class="wt-overlay-list wt2-sg -storyboard-info"
      v-if="isStoryboarding"
      key="storyboard-settings"
    >
      <div class="-title">
        <div class="wt2-text-xl">Scene Setup</div>
      </div>

      <div class="wt2-text-field -scene-title -storyboard-text">
        <div
          class="-scene-number -storyboard-input-title"
          v-text="`#${currentScene}`"
        />
        <input placeholder="What's this scene about?" v-model="sceneName" />
      </div>

      <div class="wt2-text-field -scene-description -storyboard-text">
        <div
          class="-scene-number -storyboard-input-title"
          v-text="'Description:'"
        />
        <input
          placeholder="Add description here..."
          v-model="sceneDescription"
        />
      </div>

      <media-link v-model="sceneMediaLink" label="Media"></media-link>

      <div class="wt2-text-n2 -subtitle">Frame UI Settings:</div>
      <div class="-storyboard-toggle">
        <wt-toggle v-model="sceneEditable" label-right="Allow replace asset" />
      </div>
      <div class="-storyboard-toggle">
        <wt-toggle v-model="sceneRemovable" label-right="Allow delete scene" />
      </div>
      <div class="wt2-text-n2 -subtitle">Mass Produce Settings:</div>
      <div class="-storyboard-toggle">
        <wt-toggle v-model="sceneDisabled" label-right="Skip scene" />
      </div>
      <div class="-storyboard-toggle">
        <wt-toggle
          v-model="sceneMandatory"
          label-right="Skip scene in case of missing asset"
        />
      </div>
    </div>
    <div class="wt-overlay-list wt2-sg" key="post-production">
      <div class="-title">
        <div class="wt2-text-xl -line-height">Scene Design</div>
        <div
          v-if="thereAreTranscriptToShow && !assetTranscription"
          class="wt2-color-link wt2-text-n"
          v-text="toggleTranscriptLabel"
          @click="$_onToggleTranscript"
        />
      </div>
      <transcript-layer
        :toggle="toggleTranscript"
        :marginTop="84"
        :arrowRight="68"
      />

      <div class="wt2-text-n2 -subtitle">Composition</div>
      <ul v-if="addedPages.length" class="list-unstyled">
        <li :class="page.class" v-for="page in addedPages" :key="page.id">
          <div class="wt-effect-display-icon" @click="$_onClick_Page(page)">
            <i class="wticon -effect-icon" :class="displayIcon(page)"></i>
          </div>
          <div
            class="wt-effect-display-text"
            :class="{
              disabled: page.isBlackListed || page.disabled,
              parentless: !page.smallIconClass && !page.iconClass,
            }"
            @click="$_onClick_Page(page)"
          >
            {{ page.displayText }}
            <span
              class="wt-not-applied"
              v-if="page.isBlackListed || page.disabled"
              >Not Applied</span
            >
            <i
              v-if="page.isBlackListed"
              v-tooltip="
                getTooltip('This effect is not compatible with this asset type')
              "
              class="wticon wticon-help-alt"
            ></i>
            <i
              v-if="page.disabled"
              v-tooltip="
                getTooltip(
                  'This effect is not compatible with the current theme'
                )
              "
              class="wticon wticon-help-alt"
            >
            </i>
          </div>
          <div
            class="wt-effect-remove-icon"
            @click="$_onRemovePage(page.id)"
            title="Delete"
          >
            <i class="wticon wticon-close_x wt-remove-asset-effect"></i>
          </div>
        </li>
      </ul>
      <div v-else class="-no-pages">Elements you add will show here</div>

      <asset-editor-video-transcript-controls :asset="workModel" />

      <audio-actions
        class="-audio-actions"
        :show-volume="!showVolumeDualSettings"
        :status="audioState"
        @action="onAudioActions"
      />
    </div>
  </transition-group>
</template>

<script>
import app from 'app';
import AudioActions from './AudioActions.vue';
import PageMixin from 'AssetEditor/pages/PageMixin.vue';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import Analytics from 'c/WtAnalytics';
import MediaLink from 'components/linkedFields/LinkedFieldsMediaLink.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import TranscriptMixin from 'AssetEditor/functionality/Info/TranscriptMixin';
import AssetEditorVideoTranscriptControls from 'AssetEditor/functionality/Info/AssetEditorVideoTranscriptControls.vue';

export default {
  mixins: [PageMixin, TranscriptMixin],

  data() {
    return {
      showLinkedFieldsBrowser: false,
      linkedFieldsOptions: {
        value: null,
      },
    };
  },

  components: {
    AssetEditorVideoTranscriptControls,
    AudioActions,
    MediaLink,
  },

  computed: {
    ...mapState('assetEditor', [
      'ian',
      'groups',
      'volumeMuted',
      'volumeMutedOnVoPlay',
      'pages',
    ]),
    ...mapGetters('assetEditor', [
      'activePages',
      'mergePageAndGroup',
      'isStoryboarding',
      'workModel',
      'assetModel',
      'presets',
    ]),

    sceneMediaLink: {
      get() {
        return this.workModel.assetItemSchemaDataModel.mediaLink;
      },
      set(mediaLink) {
        this.setAssetItemSchemaModel({ mediaLink });
      },
    },

    sceneRemovable: {
      get() {
        return this.workModel.assetItemSchemaDataModel.removable;
      },
      set(removable) {
        this.setAssetItemSchemaModel({ removable });
      },
    },

    sceneDisabled: {
      get() {
        return this.workModel.assetItemSchemaDataModel.disabled;
      },
      set(disabled) {
        this.setAssetItemSchemaModel({ disabled });
      },
    },

    sceneMandatory: {
      get() {
        return this.workModel.assetItemSchemaDataModel.mandatory;
      },
      set(mandatory) {
        this.setAssetItemSchemaModel({ mandatory });
      },
    },

    sceneEditable: {
      get() {
        return this.workModel.assetItemSchemaDataModel.editable;
      },
      set(editable) {
        this.setAssetItemSchemaModel({ editable });
      },
    },

    sceneName: {
      get() {
        return this.workModel.assetItemSchemaDataModel.sceneName;
      },
      set(sceneName) {
        this.setAssetItemSchemaModel({ sceneName });
      },
    },

    sceneDescription: {
      get() {
        return this.workModel.assetItemSchemaDataModel.sceneDescription;
      },
      set(sceneDescription) {
        this.setAssetItemSchemaModel({ sceneDescription });
      },
    },

    addedPages() {
      return this.activePages.map(this.mergePageAndGroup);
    },

    currentScene() {
      return this.ian.current;
    },

    audioState() {
      const rv = {};

      if (this.isFragment && this.hasValidAudio) {
        rv.video = {
          volume: this.isPaceNormal
            ? this.workModel.volumeControl.video ||
              this.workModel.onVoPlayVolumeControl.video
            : 0,
          icon: 'wticon-camera',
          text: app.culture.get('assetEditor.video'),
          muted:
            !_.isNull(this.volumeMuted.video) &&
            !_.isNull(this.volumeMutedOnVoPlay.video),
          reasonTooltip: this.isPaceNormal
            ? void 0
            : {
                placement: 'right auto',
                delay: { show: 250, hide: 0 },
                title: app.culture.get(
                  'assetEditor.cantEditVideoVolumeTooltip'
                ),
              },
        };
      }

      if (this.storyBgMusic) {
        rv.bgMusic = {
          volume: this.workModel.volumeControl.bgMusic,
          icon: 'wticon-music-alt',
          text: app.culture.get('assetEditor.bgMusic'),
          muted:
            !_.isNull(this.volumeMuted.bgMusic) &&
            !_.isNull(this.volumeMutedOnVoPlay.bgMusic),
        };
      }

      if (this.showVolumeDualSettings) {
        rv.narration = {
          volume: this.workModel.onVoPlayVolumeControl.narration,
          icon: 'wticon-mic',
          text: app.culture.get('assetEditor.narration'),
          muted: !_.isNull(this.volumeMutedOnVoPlay.narration),
          // reasonTooltip: {
          //   placement: 'right auto',
          //   delay: { show: 250, hide: 0 },
          //   title: app.culture.get('assetEditor.narrationReasonTooltip'),
          // },
        };
      }

      if (this.hasOverlayTracks) {
        rv.overlayTracks = {
          volume: 0,
          icon: 'wticon-camera',
          text: app.culture.get('assetEditor.overlayTracks'),
          reasonTooltip: {
            placement: 'right auto',
            delay: { show: 250, hide: 0 },
            title: app.culture.get('assetEditor.overlayTracksReasonTooltip'),
          },
        };
      }

      return rv;
    },
  },

  methods: {
    ...mapMutations('assetEditor', [
      'setAssetItemSchemaModel',
      'toggleVolumeMute',
      'setAGD',
    ]),
    ...mapActions('assetEditor', ['changeCurrentPage', 'removePage']),

    $_onClick_Page(page) {
      if (page.isBlackListed || page.disabled) {
        return;
      }

      this.changeCurrentPage(page.id);
    },

    getTooltip(text) {
      return {
        title: text,
        placement: 'top',
        delay: 250,
      };
    },

    $_onRemovePage(pageId) {
      this.removePage(pageId);
    },

    displayIcon(page) {
      const iconClass = page.smallIconClass || page.iconClass;

      return {
        [iconClass]: true,
        disabled: page.isBlackListed || page.disabled,
      };
    },

    onAudioActions: function ({ action, key }) {
      switch (action) {
        case 'click': {
          this.changeCurrentPage('audio');
          break;
        }

        case 'mute':
        case 'unmute': {
          this.toggleVolumeMute({ channel: key, mute: 'mute' === action });

          if ('video' === key && 'mute' === action) {
            this.setAGD({ showTranscription: false });
          }

          bus.$emit('volumeChange', {
            volumeControl: _.cloneDeep(this.workModel.volumeControl),
            onVoPlayVolumeControl: _.cloneDeep(
              this.workModel.onVoPlayVolumeControl
            ),
          });

          Analytics.trigger('analytics:audio:mute', {
            Option: `click to ${action}`,
            'Audio Type': key,
            'Asset Has Sound': this.hasValidAudio ? 'Yes' : 'No',
            'With VO': this.showVolumeDualSettings ? 'Yes' : 'No',
          });
          break;
        }

        default:
          throw new Error(`action not support! ${action}`);
      }
    },
  },
};
</script>

<style lang="scss" type="text/scss">
.-overlay-list {
  overflow-y: auto;
}

.-storyboard-info {
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);

  > div {
    position: relative;
  }

  .-title {
    display: flex;

    .wt2-story-template-tag {
      background-color: #fdb432;
      color: #fff;
      padding: 5px 10px;
      margin: 0;
      font-weight: 400;
      display: inline-block;
      font-size: 16px;
      flex-grow: 0;
      flex-shrink: 0;
    }
  }

  .-storyboard-toggle {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .-storyboard-text {
    margin-bottom: 15px;

    .-storyboard-input-title {
      position: absolute;
      bottom: 10px;
      left: 10px;
      width: 45px;
      display: flex;
      align-items: center;
      pointer-events: none;
      font-weight: 500;
      font-size: 14px;
    }

    input {
      background: transparent;
    }

    &.-scene-title {
      input {
        flex-grow: 1;
        padding-left: 45px;
      }
    }

    &.-scene-description {
      input {
        padding-left: 100px;
      }
    }
  }
}

.wt-overlay-list {
  .-title {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    user-select: none;
  }

  .-transcript-layer {
    margin-left: -60px;
  }
}

.-audio-actions {
  margin-top: 30px;
}
</style>
